import { Link } from "react-router-dom";
import { Mail, Sun, GraduationCap, CalendarClock, TrendingUp, ShieldCheck, MapPin, CheckCircle2 } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import { Faq } from "../components/Faq";
import { CtaBand } from "../components/CtaBand";
import { Seo, faqJsonLd, breadcrumbJsonLd } from "../components/Seo";
import { T, paths, EMAIL_HREF } from "../i18n";
import { CITIES } from "../data/cities";

const COPY = {
  en: {
    h1: "Security Guard Jobs in Quebec",
    lead: "Build your security career with a 100% First Nations owned company. We hire BSP-licensed guards, patrol officers, concierge agents and event staff across 60+ Quebec cities — and we invest in the people who wear our uniform.",
    whyH2: "Why Work With 1st Nations Security",
    why: [
      [Sun, "Indigenous-Owned, Community-First", "We are a 100% First Nations owned company. We actively recruit and mentor Indigenous security professionals — and we welcome every qualified candidate who shares our standards."],
      [GraduationCap, "Training & Mentorship", "New to the industry? We guide candidates through the BSP licensing path and provide site-specific onboarding that goes far beyond the mandatory training."],
      [CalendarClock, "Flexible Scheduling", "Full-time, part-time, nights, weekends, event-only — security runs around the clock, so we can build a schedule around your life."],
      [TrendingUp, "Real Career Growth", "From guard to site supervisor to operations: we promote from within and our supervisors started on post, just like you."]
    ],
    rolesH2: "Positions We Hire For",
    roles: [
      ["Security Guards", "Static posts at commercial, residential, industrial and institutional sites."],
      ["Mobile Patrol Officers", "Marked-vehicle patrols, lock checks and alarm response across assigned routes."],
      ["Concierge Security Agents", "Front-desk hospitality and protection in condos and office towers."],
      ["Event Security Staff", "Festivals, concerts and corporate events across Quebec."],
      ["Fire Watch Guards", "Code-compliant fire watch for impaired alarm and sprinkler systems."],
      ["Site Supervisors", "Experienced guards ready to lead teams and manage client sites."]
    ],
    reqH2: "What You Need to Apply",
    reqs: ["At least 18 years old and legally entitled to work in Canada", "A valid BSP agent licence — or the willingness to obtain one (we'll show you the path)", "Professional communication in French; English is a strong asset", "Reliability: security is a promise kept at 3 a.m., not a 9-to-5", "A clean background check, as required by the BSP"],
    applyH2: "How to Apply",
    applyP1: "No portals, no forms, no black holes. Email your resume (or a short note about yourself and your city) directly to our hiring team:",
    applyP2: "Use the subject line “Careers — [Your City]” so we can route you to the right regional supervisor. We reply to every application.",
    hiringH2: "Currently Hiring In",
    faq: [
      ["Do I need experience to apply?", "No. Many of our best guards started with zero security experience. If you're reliable and professional, we'll guide you through BSP licensing and train you on post."],
      ["Do you hire Indigenous candidates?", "Yes — actively. As a 100% First Nations owned company, recruiting and mentoring Indigenous security professionals is core to our mission. All qualified candidates are welcome."],
      ["Which cities are hiring?", "We recruit continuously in 60+ Quebec cities including Montreal, Quebec City, Laval, Gatineau, Longueuil, Sherbrooke, Saguenay, Lévis and Trois-Rivières. Email us your city and we'll confirm openings."],
      ["What does the BSP licence involve?", "You must be 18+, pass a background check and complete a recognized training program (about 70 hours). See our full guide: “How to Get Your BSP Security Guard Licence in Quebec”."]
    ]
  },
  fr: {
    h1: "Emplois de gardiens de sécurité au Québec",
    lead: "Bâtissez votre carrière en sécurité avec une entreprise 100 % propriété des Premières Nations. Nous embauchons des gardiens licenciés BSP, patrouilleurs, agents concierge et personnel événementiel dans plus de 60 villes du Québec — et nous investissons dans les gens qui portent notre uniforme.",
    whyH2: "Pourquoi travailler chez 1st Nations Security",
    why: [
      [Sun, "Propriété autochtone, communauté d'abord", "Nous sommes une entreprise détenue à 100 % par les Premières Nations. Nous recrutons et mentorons activement des professionnels autochtones de la sécurité — et nous accueillons tout candidat qualifié qui partage nos standards."],
      [GraduationCap, "Formation et mentorat", "Nouveau dans l'industrie? Nous guidons les candidats à travers le processus de permis du BSP et offrons une intégration propre à chaque site, bien au-delà de la formation obligatoire."],
      [CalendarClock, "Horaires flexibles", "Temps plein, temps partiel, nuits, fins de semaine, événements seulement — la sécurité fonctionne 24/7, alors nous pouvons bâtir un horaire autour de votre vie."],
      [TrendingUp, "Vraie progression de carrière", "De gardien à superviseur de site aux opérations : nous promouvons de l'interne et nos superviseurs ont commencé sur le terrain, comme vous."]
    ],
    rolesH2: "Postes pour lesquels nous embauchons",
    roles: [
      ["Gardiens de sécurité", "Postes statiques sur des sites commerciaux, résidentiels, industriels et institutionnels."],
      ["Patrouilleurs mobiles", "Patrouilles en véhicule identifié, vérifications de serrures et réponse d'alarme sur des routes assignées."],
      ["Agents de sécurité concierge", "Accueil et protection à la réception de condos et de tours de bureaux."],
      ["Personnel de sécurité événementielle", "Festivals, concerts et événements corporatifs partout au Québec."],
      ["Gardiens de surveillance incendie", "Surveillance incendie conforme au Code pour systèmes d'alarme et de gicleurs hors service."],
      ["Superviseurs de site", "Gardiens d'expérience prêts à diriger des équipes et à gérer des sites clients."]
    ],
    reqH2: "Ce qu'il faut pour postuler",
    reqs: ["Avoir au moins 18 ans et être légalement autorisé à travailler au Canada", "Un permis d'agent valide du BSP — ou la volonté de l'obtenir (nous vous montrons le chemin)", "Communication professionnelle en français; l'anglais est un atout important", "Fiabilité : la sécurité est une promesse tenue à 3 h du matin, pas un 9 à 5", "Une vérification d'antécédents satisfaisante, tel qu'exigé par le BSP"],
    applyH2: "Comment postuler",
    applyP1: "Pas de portail, pas de formulaire, pas de trou noir. Envoyez votre CV (ou un court mot sur vous et votre ville) directement à notre équipe d'embauche :",
    applyP2: "Utilisez l'objet « Carrières — [Votre ville] » pour que nous vous dirigions vers le bon superviseur régional. Nous répondons à chaque candidature.",
    hiringH2: "Nous embauchons actuellement à",
    faq: [
      ["Faut-il de l'expérience pour postuler?", "Non. Plusieurs de nos meilleurs gardiens ont débuté sans aucune expérience en sécurité. Si vous êtes fiable et professionnel, nous vous guidons à travers le permis du BSP et la formation sur le terrain."],
      ["Embauchez-vous des candidats autochtones?", "Oui — activement. À titre d'entreprise 100 % propriété des Premières Nations, recruter et mentorer des professionnels autochtones de la sécurité est au cœur de notre mission. Tous les candidats qualifiés sont les bienvenus."],
      ["Quelles villes embauchent?", "Nous recrutons en continu dans plus de 60 villes du Québec, dont Montréal, Québec, Laval, Gatineau, Longueuil, Sherbrooke, Saguenay, Lévis et Trois-Rivières. Écrivez-nous votre ville et nous confirmerons les postes ouverts."],
      ["Qu'implique le permis du BSP?", "Vous devez avoir 18 ans et plus, réussir une vérification d'antécédents et compléter un programme de formation reconnu (environ 70 heures). Consultez notre guide complet : « Comment obtenir votre permis d'agent de sécurité du BSP au Québec »."]
    ]
  }
};

export default function CareersPage({ lang }) {
  const t = T[lang];
  const p = paths(lang);
  const c = COPY[lang];

  const title = lang === "fr"
    ? "Emplois de gardiens de sécurité au Québec — Carrières | 1st Nations Security"
    : "Security Guard Jobs in Quebec — Careers | 1st Nations Security";
  const desc = lang === "fr"
    ? "Carrières en sécurité au Québec : gardiens, patrouilleurs, agents concierge et personnel événementiel. Entreprise 100 % autochtone, formation et mentorat. Postulez : admin@1stnation-security.com."
    : "Security careers in Quebec: guards, patrol officers, concierge agents and event staff. 100% Indigenous-owned company with training and mentorship. Apply: admin@1stnation-security.com.";

  return (
    <div data-testid="careers-page">
      <Seo lang={lang} title={title} description={desc} enPath="/careers" frPath="/fr/carrieres"
        jsonLd={[faqJsonLd(c.faq), breadcrumbJsonLd([[t.breadcrumbHome, p.home], [t.nav.careers, p.careers]])]} />
      <PageHeader lang={lang} eyebrow={t.brandTag} title={c.h1}
        breadcrumbs={[[t.breadcrumbHome, p.home], [t.nav.careers, null]]} />

      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
        <Reveal>
          <p className="max-w-3xl text-base leading-relaxed text-slate-300 sm:text-lg">{c.lead}</p>
        </Reveal>

        <Reveal>
          <h2 className="mt-14 font-display text-2xl font-bold tracking-tight sm:text-3xl">{c.whyH2}</h2>
        </Reveal>
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {c.why.map(([Icon, h, d], i) => (
            <Reveal key={h} delay={i * 0.07} className="rounded-2xl border border-slate-800/70 bg-card/60 p-6">
              <Icon size={24} className="text-amber-400" />
              <h3 className="mt-4 font-display text-base font-bold">{h}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-400">{d}</p>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <h2 className="mt-16 font-display text-2xl font-bold tracking-tight sm:text-3xl">{c.rolesH2}</h2>
        </Reveal>
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {c.roles.map(([h, d], i) => (
            <Reveal key={h} delay={(i % 3) * 0.06} className="rounded-xl border border-slate-800/70 bg-card/60 p-5">
              <h3 className="font-display text-base font-bold text-slate-100">{h}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-400">{d}</p>
            </Reveal>
          ))}
        </div>

        <div className="mt-16 grid gap-10 lg:grid-cols-2">
          <Reveal>
            <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">{c.reqH2}</h2>
            <ul className="mt-6 space-y-3">
              {c.reqs.map((r) => (
                <li key={r} className="flex items-start gap-2.5 text-sm text-slate-300">
                  <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-amber-400" /> {r}
                </li>
              ))}
            </ul>
            <Link to={p.guide("how-to-get-bsp-licence-quebec")} className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-amber-400 hover:text-amber-300" data-testid="careers-bsp-guide-link">
              <ShieldCheck size={15} /> {lang === "fr" ? "Guide : obtenir votre permis BSP" : "Guide: getting your BSP licence"} →
            </Link>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="rounded-3xl border border-amber-500/25 bg-gradient-to-br from-amber-500/10 to-transparent p-8" data-testid="careers-apply-card">
              <h2 className="font-display text-2xl font-bold tracking-tight">{c.applyH2}</h2>
              <p className="mt-4 text-sm leading-relaxed text-slate-300">{c.applyP1}</p>
              <a href={`${EMAIL_HREF}?subject=${encodeURIComponent(lang === "fr" ? "Carrières — " : "Careers — ")}`} className="mt-5 flex items-center justify-center gap-2 rounded-full bg-amber-500 px-6 py-3.5 text-sm font-bold text-obsidian transition-all hover:bg-amber-300 hover:shadow-[0_0_28px_rgba(245,124,0,0.4)]" data-testid="careers-apply-button">
                <Mail size={16} /> admin@1stnation-security.com
              </a>
              <p className="mt-4 text-sm leading-relaxed text-slate-400">{c.applyP2}</p>
            </div>
          </Reveal>
        </div>

        <Reveal className="mt-16">
          <h2 className="flex items-center gap-2 font-display text-2xl font-bold tracking-tight sm:text-3xl"><MapPin size={22} className="text-amber-400" /> {c.hiringH2}</h2>
          <div className="mt-6 flex flex-wrap gap-2.5">
            {CITIES.slice(0, 24).map((city) => (
              <Link key={city.slug} to={p.city(city.slug)} className="rounded-full border border-slate-800 bg-card/60 px-4 py-2 text-sm text-slate-300 transition-all hover:border-amber-500/50 hover:text-amber-300" data-testid={`careers-city-${city.slug}`}>
                {lang === "fr" ? city.fr : city.en}
              </Link>
            ))}
            <Link to={p.cities} className="rounded-full bg-amber-500 px-4 py-2 text-sm font-bold text-obsidian transition-colors hover:bg-amber-300" data-testid="careers-cities-more">+{CITIES.length - 24} →</Link>
          </div>
        </Reveal>
      </section>

      <Faq items={c.faq} title={t.faqTitle} />
      <CtaBand lang={lang} />
    </div>
  );
}
